import { coerceBooleanProperty } from '@angular/cdk/coercion'
import { AsyncPipe, DatePipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatProgressBar, ProgressBarMode } from '@angular/material/progress-bar'
import {
  ElderBasicPaneLayoutComponent,
  ElderPaneActionsComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneTitleComponent,
  ElderQuantityPipe,
  ElderToastService,
  ExceptionDetail,
  Interval,
  IsoIntervalParsePipe,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable, Subscription } from 'rxjs'
import { environment, isUtilizationTuningEnabled } from '../../../../../../environments/environment'
import { MexusContext } from '../../../../context/mexus-context.service'
import { ProductionLineAssignmentService } from '../../../../manufacturing/production-lines/assignments/production-line-assignment.service'
import { WorkBatchService } from '../../../batches/work-batch.service'
import { UtilisationProgressBarComponent } from '../../utilisation-bar/utilisation-progress-bar/utilisation-progress-bar.component'
import { UtilisationFilterBuilder } from '../../utilisation-filter-builder'
import { UtilisationCommand, UtilisationCommandType } from '../commands/utilisation-command'
import { FacilityUtilisation } from '../facility-utilisation'
import { FacilityUtilisationService } from '../facility-utilisation.service'
import { WorkCenterUtilisationViewerComponent } from '../work-center-utilisation-viewer/work-center-utilisation-viewer.component'
import { MexusViewMode } from '../../../../context/view-mode-context/mexus-view-mode'

@Component({
  selector: 'app-facility-utilisation-detail',
  templateUrl: './facility-utilisation-detail.component.html',
  styleUrls: ['./facility-utilisation-detail.component.scss'],
  imports: [
    MatProgressBar,
    MatIconButton,
    MatMenuTrigger,
    MatIcon,
    MatMenu,
    MatMenuItem,
    UtilisationProgressBarComponent,
    MatButton,
    WorkCenterUtilisationViewerComponent,
    AsyncPipe,
    DatePipe,
    ElderTranslatePipe,
    IsoIntervalParsePipe,
    ElderQuantityPipe,
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneHeaderComponent,
    ElderPaneTitleComponent,
    ElderPaneActionsComponent,
    ElderPaneContentComponent,
  ],
})
export class FacilityUtilisationDetailComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)
  private readonly _facilityUtilisation$ = new BehaviorSubject<FacilityUtilisation | null>(null)
  private _utilisationRequest: Subscription
  private facilityUtilisationService = inject(FacilityUtilisationService)
  private productionLineAssignmentService = inject(ProductionLineAssignmentService)
  private workBatchService = inject(WorkBatchService)
  private toastService = inject(ElderToastService)
  public readonly loadingBarMode$ = new BehaviorSubject<ProgressBarMode>('query')
  protected liveMode: MexusViewMode
  protected MexusViewMode = MexusViewMode

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input({ required: true })
  public set mexusContext(context: MexusContext) {
    this.loadingBarMode = 'indeterminate'
    this.cancelUtilisationRequest()
    this.liveMode = context.viewMode

    this._utilisationRequest = this.facilityUtilisationService
      .getFacilityUtilisation(
        context.facility.id,
        UtilisationFilterBuilder.start()
          .viewMode(context.viewMode)
          .scheduleInterval(context.interval)
          .timestamp(context.liveTimestamp)
          .build()
      )
      .subscribe({
        next: (facUtil) => (this.facilityUtilisation = facUtil),
        error: (err) =>
          this.toastService.error(
            'Failed to load FacilityUtilisation!' +
              ' Facility: ' +
              context.facility +
              ' Interval: ' +
              context.interval,
            err
          ),
      })
  }

  public set facilityUtilisation(utilisation: FacilityUtilisation) {
    this.loadingBarMode = 'determinate'
    this._facilityUtilisation$.next(utilisation)
  }

  public get facilityUtilisation(): FacilityUtilisation | null {
    return this._facilityUtilisation$.getValue()
  }

  public get facilityUtilisation$(): Observable<FacilityUtilisation | null> {
    return this._facilityUtilisation$.asObservable()
  }

  public set loadingBarMode(mode: ProgressBarMode) {
    this.loadingBarMode$.next(mode)
  }

  public get loadingBarMode(): ProgressBarMode {
    return this.loadingBarMode$.getValue()
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public optimiseProdRoutes() {
    const facUtil = this.facilityUtilisation

    if (facUtil) {
      this.productionLineAssignmentService
        .optimiseProductionRoutes(facUtil.facility, Interval.parse(facUtil.scheduleInterval))
        .subscribe({
          next: () => this.toastService.info('Optimisation request sent!'),
          error: (err) => {
            if (this.isErrorDetail(err?.error)) {
              const detail = <ExceptionDetail>err?.error
              this.toastService.info(detail.title)
            } else {
              console.log(err)
              this.toastService.error('Failed to send optimisation request!', err)
            }
          },
        })
    }
  }

  public assignProcessPlans(): void {
    const facUtil = this.facilityUtilisation

    if (facUtil) {
      this.prepareAndExecuteUtilisationCommand(
        UtilisationCommandType.ASSIGN_PROCESS_PLANS,
        facUtil.facility,
        Interval.parse(facUtil.scheduleInterval)
      )
    }
  }

  public assignProdRoutes(): void {
    const facUtil = this.facilityUtilisation

    if (facUtil) {
      const nullableInterval = this.isPreview() ? Interval.parse(facUtil.scheduleInterval) : null
      this.prepareAndExecuteUtilisationCommand(
        UtilisationCommandType.ASSIGN_PROD_ROUTES,
        facUtil.facility,
        nullableInterval
      )
    }
  }

  public calculateUtilisation(): void {
    const facUtil = this.facilityUtilisation

    if (facUtil) {
      this.prepareAndExecuteUtilisationCommand(
        UtilisationCommandType.CALCULATE_UTILISATION,
        facUtil.facility,
        Interval.parse(facUtil.scheduleInterval)
      )
    }
  }

  public evaluateProdRoute() {
    const facUtil = this.facilityUtilisation
    if (facUtil) {
      this.productionLineAssignmentService
        .evaluateProductionRoutes(facUtil.facility, Interval.parse(facUtil.scheduleInterval))
        .subscribe({
          next: () => this.toastService.info('Assessment request sent!'),
          error: (err) => this.toastService.error('Failed to send assessment request!', err),
        })
    }
  }

  public utilizationTuningEnabled(): boolean {
    return isUtilizationTuningEnabled()
  }

  abortFullDay(): void {
    const facUtil = this.facilityUtilisation
    if (facUtil) {
      this.workBatchService
        .abort(facUtil.facility, Interval.parse(facUtil.scheduleInterval))
        .subscribe({
          next: () => this.toastService.info('Undid production!'),
          error: (err) => this.toastService.error('Failed to undo production!', err),
        })
    }
  }

  resetFullDay(): void {
    const facUtil = this.facilityUtilisation
    if (facUtil) {
      this.productionLineAssignmentService
        .reset(facUtil.facility, Interval.parse(facUtil.scheduleInterval))
        .subscribe({
          next: () => this.toastService.info('Reset assignments!'),
          error: (err) => this.toastService.error('Failed to reset assignments!', err),
        })
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private cancelUtilisationRequest(): void {
    const sub = this._utilisationRequest
    if (sub) {
      sub.unsubscribe()
    }
  }

  private prepareAndExecuteUtilisationCommand(
    type: UtilisationCommandType,
    facilityId: string,
    scheduledInterval?: Interval
  ): void {
    this.executeUtilisationCommand({ type, facilityId, scheduledInterval })
  }

  private isPreview(): boolean {
    return this.liveMode === MexusViewMode.PREVIEW
  }

  private executeUtilisationCommand(cmd: UtilisationCommand): void {
    this.log.debug('Executing utilisation command: ', cmd)
    this.facilityUtilisationService.executeCommand(cmd).subscribe({
      next: (result) => {
        if (result && result.success) {
          if (result.message) {
            this.toastService.info(result.message)
          } else {
            this.toastService.info('messages.action_successful')
          }
        } else {
          this.toastService.error('messages.action_failed', result.message)
        }
      },
      error: (err) => this.toastService.error('messages.action_failed', err),
    })
  }

  isErrorDetail = (err?: any): err is ExceptionDetail => {
    const detail = err as ExceptionDetail
    return (
      detail?.detail !== undefined &&
      detail?.title !== undefined &&
      detail?.stacktrace !== undefined
    )
  }
}
